import type { InvestmentProperty, Scenario } from "../domain/models";

export const applyScenarioToProperty = (
  property: InvestmentProperty,
  scenario: Scenario
): InvestmentProperty => {
  if (scenario.kind === "BASELINE") return property;

  if (scenario.kind === "INTEREST_RATE_SHOCK") {
    return {
      ...property,
      loan: {
        ...property.loan,
        interestRate: Math.max(
          property.loan.interestRate + (scenario.interestRateDelta ?? 0),
          0
        )
      }
    };
  }

  if (scenario.kind === "VACANCY_SHOCK") {
    return {
      ...property,
      expenses: {
        ...property.expenses,
        vacancyRate: Math.max(
          property.expenses.vacancyRate + (scenario.vacancyDelta ?? 0),
          0
        )
      }
    };
  }

  if (scenario.kind === "RENT_GROWTH_SHIFT") {
    const rentGrowthRate = Math.max(
      property.growth.rentGrowthRate + (scenario.rentGrowthDelta ?? 0),
      -5
    );

    return {
      ...property,
      growth: {
        ...property.growth,
        rentGrowthRate
      },
      income: {
        ...property.income,
        rentGrowthRate
      }
    };
  }

  return property;
};

export const builtInScenarios = (): Scenario[] => [
  {
    id: "baseline",
    name: "Baseline",
    description: "Current assumptions without stress testing",
    kind: "BASELINE"
  },
  {
    id: "rate-plus-1",
    name: "Rate Shock +1%",
    description: "Increase rates by 1%",
    kind: "INTEREST_RATE_SHOCK",
    interestRateDelta: 1
  },
  {
    id: "rate-plus-2",
    name: "Rate Shock +2%",
    description: "Increase rates by 2%",
    kind: "INTEREST_RATE_SHOCK",
    interestRateDelta: 2
  },
  {
    id: "vacancy-plus-3",
    name: "Vacancy +3%",
    description: "Increase vacancy assumptions by 3%",
    kind: "VACANCY_SHOCK",
    vacancyDelta: 3
  },
  {
    id: "rent-slower",
    name: "Rent Growth -1.5%",
    description: "Rent growth slows by 1.5%",
    kind: "RENT_GROWTH_SHIFT",
    rentGrowthDelta: -1.5
  },
  {
    id: "rent-faster",
    name: "Rent Growth +1.5%",
    description: "Rent growth accelerates by 1.5%",
    kind: "RENT_GROWTH_SHIFT",
    rentGrowthDelta: 1.5
  },
  {
    id: "sell-year-7",
    name: "Sell In Year 7",
    description: "Assumes all properties are sold in year 7",
    kind: "SELL_IN_YEAR",
    sellYear: 7
  },
  {
    id: "extra-300",
    name: "Extra $300/m",
    description: "Apply extra monthly repayments",
    kind: "EXTRA_REPAYMENT",
    extraRepaymentMonthly: 300
  }
];
